/**
 * Active-team store (pirx-z0v4g.6).
 *
 * Holds the id of the team the user is currently acting as, persisted to
 * localStorage so it survives reloads. This lives OUTSIDE React because the API
 * request layer (`services/api/request.ts`) reads it synchronously to attach the
 * `X-Team-Id` header — the write-side half of tenant isolation. `null` means
 * personal scope (no team).
 *
 * Trust boundary: this is only a client hint. The backend never trusts it —
 * `resolveActiveTeamId` re-checks the caller's membership and returns 403 if the
 * header names a team they do not belong to. The switcher only ever offers teams
 * from the authoritative profile, and `useActiveTeam` clears a stale selection
 * when the user is no longer a member, so a removed member does not keep sending
 * a team header that would 403 every request.
 */

export const ACTIVE_TEAM_KEY = "vardic_active_team_id";

type Listener = () => void;
const listeners = new Set<Listener>();

/** Current active team id, or `null` for personal scope. */
export function getActiveTeamId(): string | null {
  try {
    const stored = localStorage.getItem(ACTIVE_TEAM_KEY);
    return stored && stored.length > 0 ? stored : null;
  } catch {
    // localStorage can throw in private-mode/SSR contexts; treat as personal.
    return null;
  }
}

/** Set (or, with `null`, clear) the active team and notify subscribers. */
export function setActiveTeamId(teamId: string | null): void {
  try {
    if (teamId && teamId.length > 0) {
      localStorage.setItem(ACTIVE_TEAM_KEY, teamId);
    } else {
      localStorage.removeItem(ACTIVE_TEAM_KEY);
    }
  } catch {
    // Ignore persistence failures; still notify so in-memory subscribers update.
  }
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Subscribe to active-team changes (for `useSyncExternalStore`).
 *
 * Notifies on both local `setActiveTeamId` writes and cross-tab `storage`
 * events for the same key. localStorage is shared across tabs of the same
 * origin, but a change in tab 2 does not fire tab 1's in-memory
 * subscribers without this bridge — so without it, tab 1 keeps displaying
 * team A in its workspace switcher while its next API request reads team B
 * from localStorage (the display/workspace mismatch the review flagged).
 *
 * One module-level storage listener is attached lazily when the first
 * subscriber appears and detached when the last one leaves, so a browser
 * event notifies the subscriber set exactly once no matter how many
 * subscribers exist. The attach/detach is SSR-guarded: in a non-browser
 * environment the in-memory notify loop still works; only the cross-tab
 * bridge is skipped.
 */

function onActiveTeamStorageEvent(event: StorageEvent): void {
  // Browsers fire `storage` events in the OTHER tabs only — the tab that
  // made the change is covered by setActiveTeamId's own notify loop.
  // A cross-tab write of our key reports it as `event.key`; a cross-tab
  // `localStorage.clear()` reports `key === null` but still removes our key,
  // so both must re-sync subscribers to the shared value. Unrelated keys
  // are ignored.
  if (event.key !== ACTIVE_TEAM_KEY && event.key !== null) return;
  for (const listener of listeners) {
    listener();
  }
}

let storageListenerAttached = false;

function attachStorageListener(): void {
  if (storageListenerAttached || typeof window === "undefined") return;
  storageListenerAttached = true;
  window.addEventListener("storage", onActiveTeamStorageEvent);
}

function detachStorageListener(): void {
  if (!storageListenerAttached || typeof window === "undefined") return;
  storageListenerAttached = false;
  window.removeEventListener("storage", onActiveTeamStorageEvent);
}

export function subscribeActiveTeam(listener: Listener): () => void {
  listeners.add(listener);
  attachStorageListener();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      detachStorageListener();
    }
  };
}
